import React, { useEffect, useState, useCallback } from "react";
import { Table, Card, Button, message, Tag, Tabs, Checkbox, Modal, Input, Spin } from "antd";
import { Download, Edit, Clock, CheckCircle, AlertCircle, Trash2, Eye } from "lucide-react";
import { getRequest, postRequest, deleteRequest } from "../../../../service/index";
import { getEmployeeData } from "../../../../utils/localStorage";
import { nowInUserTz, formatDate, formatDateTime } from "../../../../utils/dateTime";
import { dayjs } from "../../../../utils/dateTime";
import { LeftOutlined } from "@ant-design/icons";
import PayrollEdit from '../../../../modals/payrollEdit';
import ModalWrapper from '../../../../Modal';
import ExcelJS from "exceljs";
import { SearchOutlined } from "@ant-design/icons";

const PAYROLL_STATUS_TABS = ["pending", "processed", "approved"];

const getTabFromPayrollStatus = (status) => {
  const normalized = (status || "pending").toLowerCase();
  return PAYROLL_STATUS_TABS.includes(normalized) ? normalized : "pending";
};

const isTabDisabledForPayrollStatus = (tabKey, payrollStatus) => {
  const statusOrder = { pending: 0, processed: 1, approved: 2 };
  const currentStatus = getTabFromPayrollStatus(payrollStatus);
  return (statusOrder[tabKey] ?? 0) < (statusOrder[currentStatus] ?? 0);
};

const MonthlyPayrollDetails = ({ selectedPayroll, onBack }) => {
  const [employeeData, setEmployeeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [payrollEditModal, setPayrollEditModal] = useState(false);
  const [selectedPayrollId, setSelectedPayrollId] = useState(null);
  const [activeTab, setActiveTab] = useState("pending");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [searchText, setSearchText] = useState("");
  const currency = getEmployeeData()?.currency;
  const showDecimals = getEmployeeData()?.decimal !== false;
  const formatNumber = (val) => {
    const amount = val !== undefined && val !== null ? Number(val) : 0;
    return amount.toLocaleString(undefined, {
      minimumFractionDigits: showDecimals ? 2 : 0,
      maximumFractionDigits: showDecimals ? 2 : 0,
    });
  };
  
  // Permission checks
  const employeeDataForPermissions = getEmployeeData();
  const userRole = employeeDataForPermissions?.role;
  const monthlyPayrollPermissions = employeeDataForPermissions?.designation?.permissions?.Payroll?.['Monthly Payroll'] || [];
  
  const canView = userRole === "companyadmin" || monthlyPayrollPermissions.includes("view");
  const canEdit = userRole === "companyadmin" || monthlyPayrollPermissions.includes("edit");
  const canDelete = userRole === "companyadmin" || monthlyPayrollPermissions.includes("delete");
  const canProcess = userRole === "companyadmin" || monthlyPayrollPermissions.includes("process");
  const canApprove = userRole === "companyadmin" || monthlyPayrollPermissions.includes("approve");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} employees`,
  });
  const [totals, setTotals] = useState({
    totalEmployees: 0,
    totalNetSalary: 0,
    totalBasicSalary: 0,
    totalDeductions: 0,
    totalIncomeTax: 0,
    totalAllowances: 0,
    totalOvertimeSalary: 0
  });

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (searchTerm) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setPagination(prev => ({ ...prev, current: 1 }));
          fetchEmployeePayrollData(1, pagination.pageSize, searchTerm);
        }, 500); // 500ms delay
      };
    })(),
    [pagination.pageSize]
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  const fetchEmployeePayrollData = async (page = 1, limit = 10, searchTerm = searchText, statusOverride = null) => {
    setLoading(true);
    try {
      if (!canView) {
        setEmployeeData([]);
        setTotals({
          totalEmployees: 0,
          totalNetSalary: 0,
          totalBasicSalary: 0,
          totalDeductions: 0,
          totalIncomeTax: 0,
          totalAllowances: 0,
          totalOvertimeSalary: 0
        });
        return;
      }
      
      // Build query parameters
      const queryParams = new URLSearchParams({
        parentId: selectedPayroll.parentId,
        status: statusOverride || activeTab,
        page: page.toString(),
        limit: limit.toString()
      });

      // Add search parameter if provided
      if (searchTerm && searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }
      
      // Fetch data with status filter from API
      const res = await getRequest(`/api/monthlysalery/company?${queryParams.toString()}`);

      if (res?.success && Array.isArray(res.data)) {
        // Transform the data to match the table structure
        const transformedData = res.data.map(item => {
          // Find income tax from deductions array
          const incomeTaxDeduction = item.deductions?.find(d => 
            d.name?.toLowerCase().includes('income tax') || 
            d.name?.toLowerCase().includes('tax')
          );
          const incomeTax = incomeTaxDeduction?.amount || 0;

          return {
            key: item._id,
            employeeId: item.employeeDetails?.employeeCode || 'N/A',
            employeeName: item.employeeDetails?.employeeName || 'N/A',
            department: item.employeeDetails?.department || 'N/A',
            basicSalary: item.salaryCalculations?.basicSalary || 0,
            overtimeSalary: item.salaryCalculations?.overtimeSalary || 0,
            allowances: item.salaryCalculations?.totalAllowances || 0,
            deductions: item.salaryCalculations?.totalDeductions || 0,
            incomeTax: incomeTax,
            netSalary: item.salaryCalculations?.netSalary || 0,
            status: item.status || "pending",
            // Store original data for actions
            originalData: item
          };
        });

        setEmployeeData(transformedData);

        // Use totals from API response
        if (res.totals) {
          setTotals({
            totalEmployees: res.totals.totalEmployees || 0,
            totalNetSalary: res.totals.totalNetSalary || 0,
            totalBasicSalary: res.totals.totalBasicSalary || 0,
            totalDeductions: res.totals.totalDeductions || 0,
            totalIncomeTax: res.totals.totalIncomeTax || 0,
            totalAllowances: res.totals.totalAllowances || 0,
            totalOvertimeSalary: res.totals.totalOvertimeSalary || 0
          });
        } else {
          // Fallback to calculating from current page data if totals not provided
          const tabTotals = transformedData.reduce((acc, item) => ({
            totalEmployees: transformedData.length,
            totalNetSalary: acc.totalNetSalary + (item.netSalary || 0),
            totalBasicSalary: acc.totalBasicSalary + (item.basicSalary || 0),
            totalDeductions: acc.totalDeductions + (item.deductions || 0),
            totalIncomeTax: acc.totalIncomeTax + (item.incomeTax || 0),
            totalAllowances: acc.totalAllowances + (item.allowances || 0),
            totalOvertimeSalary: acc.totalOvertimeSalary + (item.overtimeSalary || 0)
          }), {
            totalEmployees: 0,
            totalNetSalary: 0,
            totalBasicSalary: 0,
            totalDeductions: 0,
            totalIncomeTax: 0,
            totalAllowances: 0,
            totalOvertimeSalary: 0
          });
          setTotals(tabTotals);
        }

        // Update pagination with data from API response
        if (res.pagination) {
          setPagination(prev => ({
            ...prev,
            current: res.pagination.currentPage || page,
            pageSize: res.pagination.limit || limit,
            total: res.pagination.totalRecords || res.data.length,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} employees (${activeTab} status)`,
          }));
        } else {
          // Fallback if pagination object is not available
          setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: limit,
            total: res.data.length,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} employees (${activeTab} status)`,
          }));
        }
      } else {
        setEmployeeData([]);
        setTotals({
          totalEmployees: 0,
          totalNetSalary: 0,
          totalBasicSalary: 0,
          totalDeductions: 0,
          totalIncomeTax: 0,
          totalAllowances: 0,
          totalOvertimeSalary: 0
        });
        setPagination(prev => ({
          ...prev,
          current: 1,
          pageSize: 10,
          total: 0
        }));
      }
    } catch (err) {
      message.error("Failed to fetch payroll data");
      setEmployeeData([]);
      setTotals({
        totalEmployees: 0,
        totalNetSalary: 0,
        totalBasicSalary: 0,
        totalDeductions: 0,
        totalIncomeTax: 0,
        totalAllowances: 0,
        totalOvertimeSalary: 0
      });
      setPagination(prev => ({
        ...prev,
        current: 1,
        pageSize: 10,
        total: 0
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedPayroll?.parentId) {
      if (selectedPayroll) {
        message.warning("SelectedPayroll does not have parentId field:", selectedPayroll);
      }
      return;
    }

    const initialTab = getTabFromPayrollStatus(selectedPayroll.status);
    setActiveTab(initialTab);
    setIsReadOnly(initialTab === "approved");
    setSelectedRowKeys([]);
    setSearchText("");
    setPagination((prev) => ({ ...prev, current: 1 }));

    if (canView) {
      fetchEmployeePayrollData(1, 10, "", initialTab);
    }
  }, [selectedPayroll, canView]);

  const handleTableChange = (paginationInfo, filters, sorter) => {
    const { current, pageSize } = paginationInfo;
    fetchEmployeePayrollData(current, pageSize, searchText);
  };

  const handleTabChange = (key) => {
    if (isTabDisabledForPayrollStatus(key, selectedPayroll?.status)) {
      return;
    }
    setActiveTab(key);
    setSelectedRowKeys([]);
    setIsReadOnly(key === "approved");
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSearchText("");
    fetchEmployeePayrollData(1, pagination.pageSize, "", key);
  };

  const payrollStatus = getTabFromPayrollStatus(selectedPayroll?.status);

  const columns = [
    {
      title: "Employee ID",
      dataIndex: "employeeId",
      key: "employeeId",
      width: 120,
      fixed: 'left',
    },
    {
      title: "Employee Name",
      dataIndex: "employeeName",
      key: "employeeName",
      width: 200,
      fixed: 'left',
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
      width: 150,
    },
    {
      title: "Basic Salary",
      dataIndex: "basicSalary",
      key: "basicSalary",
      render: (val) => `${currency || 'RS'} ${formatNumber(val)}`,
      width: 150,
    },
    {
      title: "Overtime",
      dataIndex: "overtimeSalary",
      key: "overtimeSalary",
      render: (val) => `${currency || 'RS'} ${formatNumber(val)}`,
      width: 130,
    },
    {
      title: "Allowances",
      dataIndex: "allowances",
      key: "allowances",
      render: (val) => `${currency || 'RS'} ${formatNumber(val)}`,
      width: 130,
    },
    {
      title: "Deductions",
      dataIndex: "deductions",
      key: "deductions",
      render: (val) => `${currency || 'RS'} ${formatNumber(val)}`,
      width: 130,
    },
    {
      title: "Income Tax",
      dataIndex: "incomeTax",
      key: "incomeTax",
      render: (val) => `${currency || 'RS'} ${formatNumber(val)}`,
      width: 130,
    },
    {
      title: "Net Salary",
      dataIndex: "netSalary",
      key: "netSalary",
      render: (val) => `${currency || 'RS'} ${formatNumber(val)}`,
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const getStatusColor = (status) => {
          switch (status?.toLowerCase()) {
            case "approved":
              return "green";
            case "processed":
              return "blue";
            case "pending":
              return "orange";
            default:
              return "red";
          }
        };
        
        return (
          <Tag color={getStatusColor(status)}>
            {status?.charAt(0).toUpperCase() + status?.slice(1) || "Pending"}
          </Tag>
        );
      },
      width: 100,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const isApproved = activeTab === 'approved';
        const canEditRecord = canEdit && !isApproved;
        
        return (
          <div className="flex gap-2">
            <Button
              type="text"
              size="small"
              icon={isApproved ? <Eye size={16} /> : <Edit size={16} />}
              onClick={() => handleViewDetails(record)}
              className={canEdit ? "text-blue-600 hover:text-blue-800" : "text-gray-400 cursor-not-allowed"}
              disabled={!canEdit}
              title={!canEdit ? "You don't have permission to edit" : isApproved ? "View only (approved)" : "Edit"}
            />
            <Button
              type="text"
              size="small"
              icon={<Download size={16} />}
              className={`${isGeneratingPDF ? 'text-gray-400' : 'text-black hover:text-blue-600'}`}
              onClick={() => handleDownloadPayslip(record)}
              disabled={isGeneratingPDF}
              title={isGeneratingPDF ? "Generating PDF..." : "Download PDF"}
            />
          </div>
        );
      },
      width: 120,
    },
  ];

  const handleViewDetails = (record) => {
    if (!canEdit && activeTab !== 'approved') {
      message.warning("You don't have permission to edit payroll details.");
      return;
    }
    setSelectedPayrollId(record.key);
    setIsReadOnly(activeTab === 'approved');
    setPayrollEditModal(true);
  };

  const handleClosePayrollEdit = () => {
    setPayrollEditModal(false);
    setSelectedPayrollId(null);
    setIsReadOnly(false);
    // Refresh data after editing
    fetchEmployeePayrollData(pagination.current, pagination.pageSize);
  };

  const handleDownloadPayslip = async (record) => {
    try {
      setIsGeneratingPDF(true);
      
      // Dynamically import the libraries to avoid build issues
      const jsPDF = (await import('jspdf')).default;
      const html2canvas = (await import('html2canvas')).default;
      
      message.loading(`Generating PDF payslip for ${record.employeeName}...`, 0);
      
      // Fetch detailed payroll data
      const response = await getRequest(`/api/monthlysalery/${record.key}`);
      
      if (!response?.success || !response.data) {
        throw new Error('Failed to fetch detailed payroll data');
      }
      
      const payrollData = response.data;
      const employeeDetails = payrollData.employeeDetails;
      const payrollDetails = payrollData.payrollDetails;
      const attendanceSummary = payrollDetails.attendanceSummary;
      const salaryCalculations = payrollDetails.salaryCalculations;
      const allowances = payrollDetails.allowances || [];
      const deductions = payrollDetails.deductions || [];

      // Create a temporary container for the PDF content
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '800px';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.padding = '20px';
      tempContainer.style.fontFamily = 'Arial, sans-serif';
      document.body.appendChild(tempContainer);

      // Generate the PDF content HTML
      const pdfContent = generatePDFContent(employeeDetails, payrollDetails, attendanceSummary, salaryCalculations, allowances, deductions);
      tempContainer.innerHTML = pdfContent;

      // Wait for images and fonts to load
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Convert to canvas
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 800,
        height: tempContainer.scrollHeight
      });

      // Remove temporary container
      document.body.removeChild(tempContainer);

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download the PDF
      const fileName = `payslip_${record.employeeName.replace(/[^a-zA-Z0-9]/g, '_')}_${payrollDetails.month || 'N/A'}_${payrollDetails.year || 'N/A'}.pdf`;
      pdf.save(fileName);

      message.destroy();
      message.success(`PDF payslip downloaded for ${record.employeeName}`);

    } catch (error) {
      message.destroy();
      console.error('PDF download error:', error);
      message.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Generate PDF content HTML for employee payslip
  const generatePDFContent = (employeeDetails, payrollDetails, attendanceSummary, salaryCalculations, allowances, deductions) => {
    const formatCurrency = (amount) => {
      const num = Number(amount || 0);
      return new Intl.NumberFormat('en-IN', {
        style: 'decimal',
        minimumFractionDigits: showDecimals ? 2 : 0,
        maximumFractionDigits: showDecimals ? 2 : 0,
      }).format(num);
    };

    const formatHours = (hours) => {
      if (!hours || hours === 0) return "0h 0m";
      const wholeHours = Math.floor(hours);
      const minutes = Math.round((hours - wholeHours) * 60);
      return `${wholeHours}h ${minutes}m`;
    };

    return `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; background: white;">
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #1e40af; padding-bottom: 20px;">
          <h1 style="color: #1e40af; margin: 0; font-size: 28px;">PAYSLIP</h1>
          <h2 style="color: #374151; margin: 10px 0; font-size: 20px;">${payrollDetails.month || 'N/A'}/${payrollDetails.year || 'N/A'}</h2>
        </div>

        <!-- Employee Details -->
        <div style="background: #dbeafe; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
          <h3 style="color: #1e40af; margin: 0 0 15px 0; font-size: 18px;">Employee Details</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
            <div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Employee Name:</span>
                <span style="margin-left: 10px; color: #1f2937;">${employeeDetails.employeeName || 'N/A'}</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Employee Code:</span>
                <span style="margin-left: 10px; color: #1f2937;">${employeeDetails.employeeCode || 'N/A'}</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Designation:</span>
                <span style="margin-left: 10px; color: #1f2937;">${employeeDetails.designation || 'N/A'}</span>
              </div>
            </div>
            <div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Department:</span>
                <span style="margin-left: 10px; color: #1f2937;">${employeeDetails.department || 'N/A'}</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Monthly Salary:</span>
                <span style="margin-left: 10px; color: #1f2937;">${formatCurrency(employeeDetails.monthlySalary || 0)}</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #374151;">Status:</span>
                <span style="margin-left: 10px; color: #1f2937;">${payrollDetails.status || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Attendance Summary -->
        <div style="background: #dcfce7; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
          <h3 style="color: #166534; margin: 0 0 15px 0; font-size: 18px;">Attendance Summary</h3>
          <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px;">
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Total Days</div>
              <div style="font-size: 18px; font-weight: bold; color: #1e40af;">${attendanceSummary.totalDays || 0}</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Present Days</div>
              <div style="font-size: 18px; font-weight: bold; color: #166534;">${attendanceSummary.presentDays || 0}</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Absent Days</div>
              <div style="font-size: 18px; font-weight: bold; color: #dc2626;">${attendanceSummary.missingDays || 0}</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Late Days</div>
              <div style="font-size: 18px; font-weight: bold; color: #ea580c;">${attendanceSummary.lateDays || 0}</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Leave Days</div>
              <div style="font-size: 18px; font-weight: bold; color: #1e40af;">${attendanceSummary.leaveDays || 0}</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 14px; color: #374151;">Working Hours</div>
              <div style="font-size: 18px; font-weight: bold; color: #7c3aed;">${formatHours(attendanceSummary.totalWorkingHours || 0)}</div>
            </div>
          </div>
        </div>

        <!-- Payment Details -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
          <!-- Allowances -->
          <div style="background: #dcfce7; padding: 20px; border-radius: 8px;">
            <h3 style="color: #166534; margin: 0 0 15px 0; font-size: 18px;">PAYMENT & ALLOWANCES</h3>
            <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #bbf7d0;">
              <span style="color: #374151;">Basic Salary</span>
              <span style="font-weight: bold; color: #166534;">${formatCurrency(salaryCalculations.basicSalary || 0)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #bbf7d0;">
              <span style="color: #374151;">Overtime Pay</span>
              <span style="font-weight: bold; color: #166534;">${formatCurrency(salaryCalculations.overtimeSalary || 0)}</span>
            </div>
            ${allowances.map(allowance => `
              <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #bbf7d0;">
                <span style="color: #374151;">${allowance.name || 'N/A'}</span>
                <span style="font-weight: bold; color: #166534;">${formatCurrency(allowance.amount || 0)}</span>
              </div>
            `).join('')}
            <div style="display: flex; justify-content: space-between; padding: 12px 0; border-top: 2px solid #22c55e; margin-top: 10px;">
              <span style="font-weight: bold; color: #166534;">Total Allowances</span>
              <span style="font-size: 18px; font-weight: bold; color: #166534;">${formatCurrency(salaryCalculations.totalAllowances || 0)}</span>
            </div>
          </div>

          <!-- Deductions -->
          <div style="background: #fee2e2; padding: 20px; border-radius: 8px;">
            <h3 style="color: #dc2626; margin: 0 0 15px 0; font-size: 18px;">DEDUCTIONS</h3>
            ${deductions.map(deduction => `
              <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #fecaca;">
                <span style="color: #374151;">${deduction.name || 'N/A'}</span>
                <span style="font-weight: bold; color: #dc2626;">${formatCurrency(deduction.amount || 0)}</span>
              </div>
            `).join('')}
            <div style="display: flex; justify-content: space-between; padding: 12px 0; border-top: 2px solid #ef4444; margin-top: 10px;">
              <span style="font-weight: bold; color: #dc2626;">Total Deductions</span>
              <span style="font-size: 18px; font-weight: bold; color: #dc2626;">${formatCurrency(salaryCalculations.totalDeductions || 0)}</span>
            </div>
          </div>
        </div>

        <!-- Net Salary -->
        <div style="background: #dbeafe; padding: 20px; border-radius: 8px; text-align: center;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 20px; font-weight: bold; color: #1e40af;">NET SALARY</span>
            <span style="font-size: 28px; font-weight: bold; color: #1e40af;">${formatCurrency(salaryCalculations.netSalary || 0)}</span>
          </div>
        </div>

        <!-- Footer -->
        <div style="margin-top: 30px; text-align: center; color: #6b7280; font-size: 12px; border-top: 1px solid #e5e7eb; padding-top: 20px;">
          <p>Generated on ${nowInUserTz().format('DD/MM/YYYY')} at ${nowInUserTz().format('HH:mm')}</p>
        </div>
      </div>
    `;
  };

  const handleExportAll = async () => {
    try {
      message.loading("Fetching all payroll data for export...", 0);
      
      // Fetch all data without pagination
      const response = await getRequest(
        `/api/monthlysalery/company?parentId=${encodeURIComponent(selectedPayroll.parentId)}`
      );
  
      if (response?.success && Array.isArray(response.data) && response.data.length > 0) {
        // Get all unique allowance and deduction names
        const allAllowanceNames = new Set();
        const allDeductionNames = new Set();
        
        response.data.forEach(item => {
          item.allowances?.forEach(allowance => {
            if (allowance.name) allAllowanceNames.add(allowance.name);
          });
          item.deductions?.forEach(deduction => {
            if (deduction.name) allDeductionNames.add(deduction.name);
          });
        });

        // Prepare export data with individual allowances and deductions
        const exportData = response.data.map(item => {
          const exportRow = {
            'Employee ID': item.employeeDetails?.employeeCode || 'N/A',
            'Employee Name': item.employeeDetails?.employeeName || 'N/A',
            'Designation': item.employeeDetails?.designation || 'N/A',
            'Department': item.employeeDetails?.department || 'N/A',
            'Monthly Salary': item.employeeDetails?.monthlySalary || 0,
            'Basic Salary': item.salaryCalculations?.basicSalary || 0,
            'Overtime Salary': item.salaryCalculations?.overtimeSalary || 0,
            'Total Allowances': item.salaryCalculations?.totalAllowances || 0,
            'Total Deductions': item.salaryCalculations?.totalDeductions || 0,
            'Net Salary': item.salaryCalculations?.netSalary || 0,
            'Month': item.month || 'N/A',
            'Year': item.year || 'N/A',
            'Status': item.status || 'N/A'
          };

          // Add individual allowances
          allAllowanceNames.forEach(allowanceName => {
            const allowance = item.allowances?.find(a => a.name === allowanceName);
            exportRow[`Allowance: ${allowanceName}`] = allowance?.amount || 0;
          });

          // Add individual deductions
          allDeductionNames.forEach(deductionName => {
            const deduction = item.deductions?.find(d => d.name === deductionName);
            exportRow[`Deduction: ${deductionName}`] = deduction?.amount || 0;
          });

          return exportRow;
        });
  
        // Add totals row if available
        if (response.totals) {
          exportData.push({}); // Empty row for spacing
          const totalsRow = {
            'Employee ID': 'TOTALS',
            'Employee Name': '',
            'Designation': '',
            'Department': '',
            'Monthly Salary': response.totals.totalBasicSalary || 0,
            'Basic Salary': response.totals.totalBasicSalary || 0,
            'Overtime Salary': response.totals.totalOvertimeSalary || 0,
            'Total Allowances': response.totals.totalAllowances || 0,
            'Total Deductions': response.totals.totalDeductions || 0,
            'Net Salary': response.totals.totalNetSalary || 0,
            'Month': '',
            'Year': '',
            'Status': ''
          };

          // Calculate totals for individual allowances
          allAllowanceNames.forEach(allowanceName => {
            const total = response.data.reduce((sum, item) => {
              const allowance = item.allowances?.find(a => a.name === allowanceName);
              return sum + (allowance?.amount || 0);
            }, 0);
            totalsRow[`Allowance: ${allowanceName}`] = total;
          });

          // Calculate totals for individual deductions
          allDeductionNames.forEach(deductionName => {
            const total = response.data.reduce((sum, item) => {
              const deduction = item.deductions?.find(d => d.name === deductionName);
              return sum + (deduction?.amount || 0);
            }, 0);
            totalsRow[`Deduction: ${deductionName}`] = total;
          });

          exportData.push(totalsRow);
        }
  
        // Create workbook using ExcelJS
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Monthly Payroll');
        
        // Add headers
        const headers = Object.keys(exportData[0]);
        worksheet.addRow(headers);
        
        // Style the header row
        const headerRow = worksheet.getRow(1);
        headerRow.eachCell((cell) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF366092' } // Blue background
          };
          cell.font = {
            bold: true,
            color: { argb: 'FFFFFFFF' } // White text
          };
          cell.alignment = {
            horizontal: 'center',
            vertical: 'middle'
          };
          cell.border = {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
          };
        });
        
        // Add data rows
        exportData.forEach((row, index) => {
          const dataRow = worksheet.addRow(Object.values(row));
          
          // Check if this is an approved employee
          const isApproved = row['Status']?.toLowerCase() === 'approved';
          
          // Style data rows
          dataRow.eachCell((cell) => {
            cell.alignment = {
              horizontal: 'center',
              vertical: 'middle'
            };
            cell.border = {
              top: { style: 'thin', color: { argb: 'FF000000' } },
              bottom: { style: 'thin', color: { argb: 'FF000000' } },
              left: { style: 'thin', color: { argb: 'FF000000' } },
              right: { style: 'thin', color: { argb: 'FF000000' } }
            };
            
            // Apply green background for approved employees
            if (isApproved) {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF90EE90' } // Light green background
              };
              cell.font = {
                bold: true,
                color: { argb: 'FF006400' } // Dark green text
              };
            }
          });
        });
        
        // Set column widths
        headers.forEach((header, index) => {
          const column = worksheet.getColumn(index + 1);
          if (header === 'Employee ID') column.width = 15;
          else if (header === 'Employee Name') column.width = 25;
          else if (header === 'Designation') column.width = 20;
          else if (header === 'Department') column.width = 20;
          else if (header === 'Monthly Salary') column.width = 15;
          else if (header === 'Basic Salary') column.width = 15;
          else if (header === 'Overtime Salary') column.width = 15;
          else if (header === 'Total Allowances') column.width = 18;
          else if (header === 'Total Deductions') column.width = 18;
          else if (header === 'Net Salary') column.width = 15;
          else if (header === 'Month') column.width = 10;
          else if (header === 'Year') column.width = 10;
          else if (header === 'Status') column.width = 12;
          else if (header.startsWith('Allowance:')) column.width = 18;
          else if (header.startsWith('Deduction:')) column.width = 18;
          else column.width = 15;
        });
        
        // Format number columns
        const numberColumns = [
          'Monthly Salary', 'Basic Salary', 'Overtime Salary', 
          'Total Allowances', 'Total Deductions', 'Net Salary'
        ];
        
        // Add all allowance and deduction columns to number formatting
        headers.forEach(header => {
          if (header.startsWith('Allowance:') || header.startsWith('Deduction:')) {
            numberColumns.push(header);
          }
        });
        
        numberColumns.forEach(colName => {
          const colIndex = headers.indexOf(colName);
          if (colIndex !== -1) {
            const column = worksheet.getColumn(colIndex + 1);
            column.numFmt = showDecimals ? '#,##0.00' : '#,##0';
          }
        });
        
        // Style totals row if available
        if (response.totals) {
          const totalsRow = worksheet.getRow(exportData.length + 1);
          totalsRow.eachCell((cell) => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFFFE699' } // Yellow background
            };
            cell.font = {
              bold: true,
              color: { argb: 'FF000000' } // Black text
            };
          });
        }
        
        // Generate filename with current date
        const filename = `Monthly_Payroll_${new Date().toISOString().split('T')[0]}.xlsx`;
        
        // Save the file using ExcelJS
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
        
        message.destroy();
        message.success(`Exported ${response.data.length} records to ${filename}`);
      } else {
        message.destroy();
        message.warning(response?.success ? 'No data available' : 'Export failed');
      }
    } catch (error) {
      message.destroy();
      console.error('Export error:', error);
      message.error('Export failed. Please try again.');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select at least one record");
      return;
    }
    
    // Check permissions based on action
    if (action === 'Process' && !canProcess) {
      message.warning("You don't have permission to process payroll records.");
      return;
    }
    
    if (action === 'Approve' && !canApprove) {
      message.warning("You don't have permission to approve payroll records.");
      return;
    }
    
    try {
      // Map action to status
      const statusMap = {
        'Approve': 'approved',
        'Process': 'processed'
      };
      
      const status = statusMap[action];
      if (!status) {
        message.error(`Invalid action: ${action}`);
        return;
      }
      
      const payload = {
        status: status,
        salaryIds: selectedRowKeys
      };
      
      const response = await postRequest('/api/monthlysalery/bulk-status', payload);
      
      if (response?.success) {
        message.success(`${action} action completed successfully for ${selectedRowKeys.length} records`);
        setSelectedRowKeys([]); // Clear selections
        // Refresh the data to show updated status
        fetchEmployeePayrollData(pagination.current, pagination.pageSize);
      } else {
        message.error(response?.message || `Failed to ${action.toLowerCase()} records`);
      }
    } catch (error) {
      console.error('Bulk action error:', error);
      message.error(`Failed to ${action.toLowerCase()} records. Please try again.`);
    }
  };

  const handleDeleteSelected = () => {
    if (!canDelete) {
      message.warning("You don't have permission to delete payroll records.");
      return;
    }
    
    if (selectedRowKeys.length === 0) {
      message.warning("Please select at least one record");
      return;
    }
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    try {
      // Join the IDs with %2C (URL encoded comma)
      const idsParam = selectedRowKeys.join('%2C');
      const response = await deleteRequest(`/api/monthlysalery/company?ids=${idsParam}`);
      
      if (response?.success) {
        message.success(`Successfully deleted ${selectedRowKeys.length} record(s)`);
        setSelectedRowKeys([]); // Clear selections
        setDeleteModalVisible(false);
        // Refresh the data to show updated list
        fetchEmployeePayrollData(pagination.current, pagination.pageSize);
      } else {
        message.error(response?.message || 'Failed to delete records');
      }
    } catch (error) {
      console.error('Delete error:', error);
      message.error('Failed to delete records. Please try again.');
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalVisible(false);
  };

  if (!selectedPayroll) {
    return (
      <div className="px-3 py-5">
        <div className="text-center py-8">
          <p className="text-gray-500">No payroll selected</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="px-3 py-5">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">

          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Monthly Payroll Details
            </h2>
            <p className="text-gray-600">
              {selectedPayroll.month && selectedPayroll.year ? `${nowInUserTz().year(selectedPayroll.year).month(selectedPayroll.month - 1).format('MMMM YYYY')}` : 'N/A'} • {totals.totalEmployees} Employees • {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Status
            </p>
          </div>
        </div>
        <div className="flex flex-col justify-between gap-2">
          <Button  onClick={onBack} icon={<LeftOutlined />}>Back to Payroll</Button>
          <Button
            type="primary"
            icon={<Download size={16} />}
            onClick={handleExportAll}
            className="bg-primary"
          >
            Export All
          </Button>
        </div>

      </div>



      {/* Selected Records Actions */}
      {selectedRowKeys.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-blue-700 font-medium">
                {selectedRowKeys.length} record(s) selected
              </span>
            </div>
            <div className="flex gap-2">
              {activeTab === 'pending' && canProcess && (
                <Button
                  size="small"
                  onClick={() => handleBulkAction('Process')}
                  className="bg-blue-500 text-white hover:bg-blue-600"
                >
                  Process Selected
                </Button>
              )}
              {activeTab === 'processed' && canApprove && (
                <Button
                  size="small"
                  onClick={() => handleBulkAction('Approve')}
                  className="bg-green-500 text-white hover:bg-green-600"
                >
                  Approve Selected
                </Button>
              )}
              {canDelete && (
                <Button
                  size="small"
                  danger
                  onClick={handleDeleteSelected}
                  icon={<Trash2 size={14} />}
                >
                  Delete Selected
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card className="bg-green-50 border-green-200">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-green-600 text-sm">Total Net Salary</p>
              <h3 className="text-xl font-bold text-green-800">
                {currency || 'RS'} {formatNumber(totals.totalNetSalary)}
              </h3>
            </div>
          </div>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-blue-600 text-sm">Total Basic Salary</p>
              <h3 className="text-xl font-bold text-blue-800">
                {currency || 'RS'} {formatNumber(totals.totalBasicSalary)}
              </h3>
            </div>
          </div>
        </Card>
        <Card className="bg-orange-50 border-orange-200">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-orange-600 text-sm">Total Deductions</p>
              <h3 className="text-xl font-bold text-orange-800">
                {currency || 'RS'} {formatNumber(totals.totalDeductions)}
              </h3>
            </div>
          </div>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-purple-600 text-sm">Income Tax</p>
              <h3 className="text-xl font-bold text-purple-800">
                {currency || 'RS'} {formatNumber(totals.totalIncomeTax)}
              </h3>
            </div>
          </div>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-yellow-600 text-sm">Total Allowances</p>
              <h3 className="text-xl font-bold text-yellow-800">
                {currency || 'RS'} {formatNumber(totals.totalAllowances)}
              </h3>
            </div>
          </div>
        </Card>
      </div>
      {/* Search Bar */}
      <div className="w-full flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Input
            placeholder="Search by employee name or ID"
            value={searchText}
            onChange={handleSearchChange}
            prefix={<SearchOutlined className="text-gray-500" />}
            className="px-4 py-3 rounded-lg h-10"
            allowClear
          />
        </div>
        {/* <Button
          onClick={() => {
            setSearchText("");
            setPagination(prev => ({ ...prev, current: 1 }));
            fetchEmployeePayrollData(1, pagination.pageSize, "");
          }}
          className="text-gray-600 hover:text-gray-800"
        >
          Reset
        </Button> */}
      </div>
      {/* Employee Table */}
      <Tabs 
        activeKey={activeTab} 
        onChange={handleTabChange} 
        className="mb-6"
        items={[
          {
            key: 'pending',
            disabled: isTabDisabledForPayrollStatus('pending', payrollStatus),
            label: (
              <span className="flex items-center gap-2">
                <Clock size={16} />
                Pending
              </span>
            ),
            children: (
              <>
                <Spin spinning={loading} size="large">
                  <Table
                    columns={columns}
                    dataSource={canView ? employeeData : []}
                    loading={loading}
                    scroll={{ x: "max-content" }}
                    pagination={{
                      ...pagination,
                      total: canView ? pagination.total : 0
                    }}
                    onChange={handleTableChange}
                    rowKey="key"
                    className="bg-white"
                    rowSelection={canView ? {
                      selectedRowKeys: selectedRowKeys,
                      onChange: (selectedRowKeys, selectedRows) => {
                        setSelectedRowKeys(selectedRowKeys);
                      },
                      getCheckboxProps: (record) => ({
                        name: record.key,
                      }),
                    } : undefined}
                  />
                </Spin>
                {!canView && !loading && (
                  <div className="text-center text-gray-500 py-4">You don't have permission to view payroll data.</div>
                )}
              </>
            )
          },
          {
            key: 'processed',
            disabled: isTabDisabledForPayrollStatus('processed', payrollStatus),
            label: (
              <span className="flex items-center gap-2">
                <AlertCircle size={16} />
                Processed
              </span>
            ),
            children: (
              <>
                <Spin spinning={loading} size="large">
                  <Table
                    columns={columns}
                    dataSource={canView ? employeeData : []}
                    loading={loading}
                    scroll={{ x: "max-content" }}
                    pagination={{
                      ...pagination,
                      total: canView ? pagination.total : 0
                    }}
                    onChange={handleTableChange}
                    rowKey="key"
                    className="bg-white"
                    rowSelection={canView ? {
                      selectedRowKeys: selectedRowKeys,
                      onChange: (selectedRowKeys, selectedRows) => {
                        setSelectedRowKeys(selectedRowKeys);
                      },
                      getCheckboxProps: (record) => ({
                        name: record.key,
                      }),
                    } : undefined}
                  />
                </Spin>
                {!canView && !loading && (
                  <div className="text-center text-gray-500 py-4">You don't have permission to view payroll data.</div>
                )}
              </>
            )
          },
          {
            key: 'approved',
            disabled: isTabDisabledForPayrollStatus('approved', payrollStatus),
            label: (
              <span className="flex items-center gap-2">
                <CheckCircle size={16} />
                Approved
              </span>
            ),
            children: (
              <>
                <Spin spinning={loading} size="large">
                  <Table
                    columns={columns}
                    dataSource={canView ? employeeData : []}
                    loading={loading}
                    scroll={{ x: "max-content" }}
                    pagination={{
                      ...pagination,
                      total: canView ? pagination.total : 0
                    }}
                    onChange={handleTableChange}
                    rowKey="key"
                    className="bg-white"
                  />
                </Spin>
                {!canView && !loading && (
                  <div className="text-center text-gray-500 py-4">You don't have permission to view payroll data.</div>
                )}
              </>
            )
          }
        ]}
      />
    </div>
    <ModalWrapper
      open={payrollEditModal}
      onRequestClose={handleClosePayrollEdit}
      title={isReadOnly ? "Payroll Details (Read Only)" : "Payroll Details"}
    >
      <PayrollEdit onClose={handleClosePayrollEdit} payrollId={selectedPayrollId} readOnly={isReadOnly} />
    </ModalWrapper>

    {/* Delete Confirmation Modal */}
    <Modal
      title="Confirm Delete"
      open={deleteModalVisible}
      onOk={confirmDelete}
      onCancel={handleCancelDelete}
      okText="Delete"
      cancelText="Cancel"
      okButtonProps={{ danger: true }}
    >
      <p>Are you sure you want to delete {selectedRowKeys.length} selected record(s)?</p>
      <p className="text-red-500 text-sm">This action cannot be undone.</p>
    </Modal>
    </>
  );
};

export default MonthlyPayrollDetails;
